import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, pluck, switchMap, withLatestFrom } from 'rxjs/operators';

import {
  getAggregatedJobData,
  getAggregationsState,
} from '@collections/aggregations/aggregations.selectors';
import { getJobsSelectedId } from '@collections/jobs/jobs.selectors';
import {
  jobMatchesNumberForJobSuccess,
  loadJobMatchesCounts,
} from '@modules/job-page/store/job.actions';
import { AbstractAggregation, AggregationParams, Candidate } from '@shared/models';
import { ApiRequest, SearchCandidatesService } from '@shared/services';

import {
  AggregationLoadHiringManager,
  AggregationLoadHiringManagerFail,
  AggregationLoadHiringManagerSuccess,
  loadActiveJobsCount,
  loadActiveJobsCountFail,
  loadActiveJobsCountSuccess,
  LoadAggregatedJobsData,
  LoadAggregatedJobsDataForJob,
  LoadAggregatedJobsDataSuccess,
  loadAggregatedMatches,
  loadAggregatedMatchesFail,
  loadAggregatedMatchesSuccess,
  loadTotalDirectPerPhase,
  loadTotalDirectPerPhaseFail,
  loadTotalDirectPerPhaseSuccess,
} from './aggregations.actions';
import { JobsPageFilterType } from '@modules/jobs-page/store/filter/filter.reducer';
import { ReportPipelineService } from '@shared/services/report-pipeline.service';
import { AggregatedJobService } from '@shared/services/aggregated-job.service';

@Injectable()
export class AggregationsEffects {
  loadHiringManager$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AggregationLoadHiringManager),
      pluck('payload'),
      mergeMap(({ params }: ApiRequest<AggregationParams>) =>
        this.service.hiringManagerAggregation({ params }).pipe(
          map((aggregation: AbstractAggregation<Candidate>) =>
            AggregationLoadHiringManagerSuccess({
              payload: {
                ...aggregation,
                jobId: params.jobId,
              },
            }),
          ),
          catchError((error) =>
            of(
              AggregationLoadHiringManagerFail({
                error,
                jobId: params.jobId,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  LoadAggregatedJobsData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadAggregatedJobsData),
      withLatestFrom(this.store.select(getAggregationsState), this.store.select(getJobsSelectedId)),
      mergeMap(
        ([
          { jobsIds, loadAggregationForSingleJob, updateSingleAggregatedJobData, filters },
          aggregationState,
          jobSelectedId,
        ]) => {
          const jobsIdsToSend = !!updateSingleAggregatedJobData
            ? [+jobSelectedId]
            : // @ts-ignore
              jobsIds.filter((x) => !aggregationState.ids.includes(x));
          return this.aggregatedJobService.getAggregatedJobsData(jobsIdsToSend, filters).pipe(
            map((response) =>
              LoadAggregatedJobsDataForJob({
                aggregatedJobsData: response,
                loadAggregationForSingleJob,
              }),
            ),
          );
        },
      ),
    ),
  );

  LoadAggregatedJobsDataForJob$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadAggregatedJobsDataForJob),
      withLatestFrom(this.store.select(getAggregatedJobData)),
      switchMap(([{ aggregatedJobsData, loadAggregationForSingleJob }, aggregatedJobData]) => {
        let mappedAggregatedJobsData;
        if (!!loadAggregationForSingleJob) {
          mappedAggregatedJobsData = {
            ...(!!aggregatedJobData ? aggregatedJobData : aggregatedJobsData[0]),
          };
          delete mappedAggregatedJobsData.totalDirect;
        }
        return !!loadAggregationForSingleJob
          ? !!aggregatedJobData
            ? [jobMatchesNumberForJobSuccess({ matchesNumbers: mappedAggregatedJobsData })]
            : [
                LoadAggregatedJobsDataSuccess({ aggregatedJobsData }),
                jobMatchesNumberForJobSuccess({ matchesNumbers: mappedAggregatedJobsData }),
                loadJobMatchesCounts(),
              ]
          : [LoadAggregatedJobsDataSuccess({ aggregatedJobsData })];
      }),
    ),
  );

  loadTotalDirectPerPhase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTotalDirectPerPhase),
      switchMap(({ filters }) => {
        const params = {
          jobFunction: filters.jobFunction,
          jobDepartment: filters.jobDepartment,
          jobStatus: filters.jobStatus,
          searchByRecruiter: filters.searchByRecruiter,
          jobAssignedTo: filters.jobAssignedTo,
        };
        return this.reportPipelineService.get({ url: 'total-direct-per-phase', params }).pipe(
          map((totalDirectPerPhase) => loadTotalDirectPerPhaseSuccess({ totalDirectPerPhase })),
          catchError((error) => of(loadTotalDirectPerPhaseFail({ error }))),
        );
      }),
    ),
  );

  loadActiveJobsCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadActiveJobsCount),
      switchMap(({ filters }) => {
        const params = {
          jobFunction: filters.jobFunction,
          jobDepartment: filters.jobDepartment,
          jobStatus: filters.jobStatus,
          searchByRecruiter: filters.searchByRecruiter,
          jobAssignedTo: !!filters.jobAssignedTo ? filters.jobAssignedTo : JobsPageFilterType.ALL,
        };
        return this.reportPipelineService.get({ url: 'active-job-count', params }).pipe(
          map((activeJobsCount) => loadActiveJobsCountSuccess({ activeJobsCount })),
          catchError((error) => of(loadActiveJobsCountFail({ error }))),
        );
      }),
    ),
  );

  loadAggregatedMatches$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAggregatedMatches),
      switchMap(({ filters }) => {
        const params = {
          jobFunction: filters.jobFunction,
          jobDepartment: filters.jobDepartment,
          jobStatus: filters.jobStatus,
          searchByRecruiter: filters.searchByRecruiter,
          jobAssignedTo: !!filters.jobAssignedTo ? filters.jobAssignedTo : JobsPageFilterType.ALL,
        };
        return this.reportPipelineService.get({ url: 'aggregated-matches', params }).pipe(
          map((aggregatedMatches) => loadAggregatedMatchesSuccess({ aggregatedMatches })),
          catchError((error) => of(loadAggregatedMatchesFail({ error }))),
        );
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private service: SearchCandidatesService,
    private store: Store<{}>,
    private reportPipelineService: ReportPipelineService,
    private aggregatedJobService: AggregatedJobService,
  ) {}
}
